import type {
  ListTablePagination,
  ListTableProps,
  ListTableSort,
} from "./list-table.type";
import type { ListItem } from "@/modules/list";

import { Table } from "antd";
import classNames from "classnames";
import { useRef, useMemo } from "react";

import { useListContext } from "@/modules/list";

import ListTableActions from "./list-table-actions";
import ListTableBatchButtons, {
  isBatchModeActive,
} from "./list-table-batch-buttons";
import ListTableLazyHandler from "./list-table-lazy-handler";
import {
  useListTableColumns,
  useListTableRowClassName,
  useListTableSelectedRows,
} from "./list-table.hook";

import "./list-table.style.less";

// ListTable should be used inside <ListProvider>
const ListTable = <Item extends ListItem>({
  columns = [],
  modelKey,
  onAddButton,
  addButtonText,
  extraActions,
  showExportButton,
  onBatchEditClick,
  onBatchDeleteClick,
  onBatchDissociateClick,
  onSelectedItemsChange,
  ...tableProps
}: ListTableProps<Item>) => {
  const tableRef = useRef<HTMLDivElement>(null);

  const { lazy, data, total, loading, listParams, onListTableChange } =
    useListContext<Item>();

  const { getRowClassName } = useListTableRowClassName(modelKey);

  const isBatchMode = isBatchModeActive<Item>({
    onBatchEditClick,
    onBatchDeleteClick,
    onBatchDissociateClick,
  });

  const { rowSelection, selectedItems } = useListTableSelectedRows<Item>({
    onSelectedItemsChange,
    isBatchMode,
  });

  const formattedColumns = useListTableColumns({ columns });

  const pagination = useMemo((): ListTablePagination | false => {
    if (lazy) return false;
    // TODO IMP-866 send back-end pagination data instead of list params pagination
    return {
      ...listParams.pagination,
      total,
      showSizeChanger: true,
      showTotal: (total, [from, to]) => `${from}-${to} / ${total}`,
    };
  }, [lazy, listParams.pagination, total]);

  return (
    <>
      <ListTableLazyHandler tableRef={tableRef} />
      <ListTableActions
        onAddButton={onAddButton}
        addButtonText={addButtonText}
        showExportButton={showExportButton}
        extraActions={extraActions}
        batchButtons={
          <ListTableBatchButtons
            divider={<div className="list-table-actions-divider" />}
            selectedItems={selectedItems}
            onBatchEditClick={onBatchEditClick}
            onBatchDeleteClick={onBatchDeleteClick}
            onBatchDissociateClick={onBatchDissociateClick}
          />
        }
      />
      <div
        ref={tableRef}
        className={classNames("list-table", {
          "list-table-lazy-loading": lazy ? true : false,
        })}
      >
        <Table
          rowKey="id"
          dataSource={data}
          loading={loading}
          onChange={(pagination, filters, sorter) => {
            const sort = sorter as ListTableSort<Item>;
            onListTableChange(pagination, filters, sort);
          }}
          rowClassName={getRowClassName}
          pagination={pagination}
          rowSelection={rowSelection}
          size="small"
          // Never use `scroll.y`, it breaks the table header and table body columns
          // alignment/overlap (because it actually create 2 separated tables, one
          // for thead and one for tbody...). For lazy loading we use `max-height`
          // on `.app-table-content` instead.
          scroll={{ x: "max-content" }}
          {...tableProps}
          columns={formattedColumns}
          prefixCls="app-table" // Keep it, this is needed for Jest tests to work
        />
      </div>
    </>
  );
};

export default ListTable;
